import { getProductType, getVoucherType } from '../../core/hooks/useAnalytics/getLegacyEventData';
import { Deal } from '../../core/types/Deal';
import { DealData } from '../../core/types/DealData';
import { ModelInfo } from '../../core/types/ModelInfo';
import { ProductType, VoucherType } from '../../types/ProductTypes';

export type EventProduct = {
  id?: string;
  globalId?: string;
  originalLink?: string;
  link?: string;
  name?: string;
  currency?: string;
  previousPrice?: string;
  price?: string;
  preorder?: boolean;
  inStock?: boolean;
  revenueId?: string;
  startTime?: string;
  endTime?: string;
  type: ProductType;
  merchant: {
    id?: string;
    name?: string;
    network?: string;
    url?: string;
  };
  model: {
    id?: string;
    name?: string;
    brand?: string;
    parentModelName?: string;
  };
  labels: { type: string; value: string }[];
  promos: { type: string; value: string }[];
  contract?: {
    provider: string;
    minutes?: number;
    texts?: number;
    data?: number;
    length: number;
    monthlyPrice: string;
  };
  voucher?: {
    type?: VoucherType;
    audience?: string;
    percentageSaving?: number;
    moneySaving?: number;
  };
};

export const dealToProduct = (deal: Deal, modelInfo: ModelInfo): EventProduct => ({
  id: String(deal.id),
  globalId: deal.product_key,
  originalLink: deal.offer.rawLink,
  link: deal.offer.link,
  name: deal.offer.name,
  currency: deal.offer.currency_iso,
  previousPrice: deal.offer.was_price ? String(deal.offer.was_price) : undefined,
  price: deal.offer.price,
  preorder: deal.offer.preorder,
  inStock: deal.offer.in_stock,
  revenueId: deal.offer.custom_tracking_id,
  startTime: deal.start_time ? new Date(deal.start_time * 1000).toISOString() : undefined,
  endTime: deal.end_time ? new Date(deal.end_time * 1000).toISOString() : undefined,
  type: getProductType(deal.product_type),
  merchant: {
    id: deal.merchant.id ? String(deal.merchant.id) : undefined,
    name: deal.merchant.name,
    network: deal.an,
    url: deal.merchant.url,
  },
  model: {
    id: String(deal.model_id),
    name: deal.model,
    brand: modelInfo[deal.model_id]?.brand ?? undefined,
    parentModelName: modelInfo[deal.model_id]?.parents?.[0]?.name,
  },
  labels: (deal.labels_formatted ?? []).map((label) => ({
    type: label.type,
    value: String(label.value),
  })),
  promos: (deal.promos ?? []).map((promo) => ({
    type: promo.type,
    value: promo.value,
  })),
  ...(deal.contract && {
    contract: {
      provider: deal.contract.network,
      minutes: deal.contract.minutes ?? undefined,
      texts: deal.contract.texts ?? undefined,
      data: deal.contract.data ?? undefined,
      length: deal.contract.length,
      monthlyPrice: deal.contract.monthly_price,
    },
  }),
  voucher: {
    type: getVoucherType(deal.product_type) ?? undefined,
    audience: deal.offer.audience ?? undefined,
    percentageSaving: deal.offer.percentage_saving ?? undefined,
    moneySaving: deal.offer.money_saving ?? undefined,
  },
});

export const dealDataToProducts = (dealData: DealData): EventProduct[] => {
  const { deals, modelInfo } = dealData;

  return deals.map((deal) => dealToProduct(deal, modelInfo));
};
